import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getPropertiesDashboard, runCalendarScript, getPropertyStatus, getPropertyImageUrl, getLockStatus, API_BASE_URL } from '../services/api';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';

// Styles
const styles = {
  container: {
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: 'var(--bg-page)',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  titleWrap: {
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: 0,
    letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--text-tertiary)',
    margin: '2px 0 0 0',
    fontWeight: '400',
  },
  refreshIcon: {
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--border-light)',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.15s ease',
  },
  refreshIconDisabled: {
    width: '34px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--border-light)',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-subtle)',
    color: 'var(--text-muted)',
    cursor: 'not-allowed',
    fontSize: '14px',
  },
  statsBar: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    flex: 1,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)',
    border: '1px solid var(--border-default)',
    borderLeft: '4px solid transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statIcon: {
    fontSize: '24px',
    lineHeight: 1,
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: '32px',
    fontWeight: '700',
    lineHeight: 1.1,
    marginBottom: '2px',
  },
  statLabel: {
    fontSize: '13px',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  filterButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text-tertiary)',
    border: '1px solid var(--border-input)',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: 'none',
  },
  filterButtonActive: {
    padding: '8px 16px',
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    border: '1px solid var(--color-primary)',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  },
  filterSelect: {
    padding: '8px 12px',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-input)',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    minWidth: '140px',
    outline: 'none',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  searchInput: {
    padding: '10px 16px',
    width: '280px',
    borderRadius: '8px',
    border: '1px solid var(--border-input)',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: 'var(--bg-input)',
    color: 'var(--text-primary)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
  },
  card: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)',
    border: '1px solid var(--border-default)',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  cardHover: {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
  cardImageContainer: {
    position: 'relative',
    width: '100%',
    height: '140px',
    overflow: 'hidden',
    backgroundColor: 'var(--bg-button-secondary)',
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  },
  cardImageHover: {
    transform: 'scale(1.05)',
  },
  cardImagePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bg-image-placeholder)',
    color: 'var(--text-faint)',
    fontSize: '40px',
  },
  cardContent: {
    padding: '16px 20px 20px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  propertyInfo: {
    flex: 1,
  },
  propertyName: {
    fontSize: '18px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    margin: '0 0 4px 0',
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '60%',
    background: 'linear-gradient(transparent 0%, rgba(0,0,0,0.5) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
  },
  statusBadgeOverlay: {
    position: 'absolute',
    bottom: '10px',
    left: '12px',
    zIndex: 2,
    padding: '5px 10px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backdropFilter: 'blur(4px)',
  },
  propertyAddress: {
    fontSize: '13px',
    color: 'var(--text-tertiary)',
    margin: 0,
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statusOccupied: {
    backgroundColor: 'var(--badge-red-bg)',
    color: 'var(--badge-red-text)',
  },
  statusVacant: {
    backgroundColor: 'var(--badge-green-bg)',
    color: 'var(--badge-green-text)',
  },
  statusUnknown: {
    backgroundColor: 'var(--bg-subtle)',
    color: '#6b7280',
  },
  cardBody: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px 16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '4px 0',
    borderBottom: '1px solid var(--border-light)',
  },
  fieldLabel: {
    fontSize: '10px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  fieldValue: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid var(--border-light)',
  },
  propertyId: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    backgroundColor: 'var(--bg-subtle)',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  viewLink: {
    fontSize: '13px',
    color: 'var(--color-primary)',
    fontWeight: '500',
    textDecoration: 'none',
  },
  timesRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid var(--border-light)',
    gap: '6px',
    flexWrap: 'wrap',
  },
  timeItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  timeLabel: {
    fontSize: '10px',
    fontWeight: '500',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  timeValue: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-tertiary)',
  },
  timeSeparator: {
    color: 'var(--text-muted)',
    fontSize: '10px',
    margin: '0 4px',
    opacity: 0.5,
  },
  cleanerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '10px',
    padding: '8px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    borderLeft: '3px solid transparent',
    transition: 'all 0.2s ease',
  },
  cleanerAssigned: {
    backgroundColor: '#f0fdf4',
    color: '#166534',
    borderLeftColor: '#22c55e',
  },
  cleanerUnassigned: {
    backgroundColor: '#fef9c3',
    color: '#854d0e',
    borderLeftColor: '#eab308',
  },
  assignmentDate: {
    fontSize: '12px',
    opacity: 0.8,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    color: 'var(--text-tertiary)',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid var(--spinner-track)',
    borderTop: '3px solid var(--color-primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  renderTime: {
    textAlign: 'right',
    fontSize: '11px',
    color: 'var(--text-muted)',
    marginTop: '24px',
    opacity: 0.6,
    fontFamily: 'monospace',
    letterSpacing: '0.3px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--text-muted)',
  },
};

// Add keyframes for animations
const animationKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes fadeInText {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }
  @keyframes lowBatteryPulse {
    0%, 100% { box-shadow: 0 0 4px rgba(239,68,68,0.2); }
    50% { box-shadow: 0 0 12px rgba(239,68,68,0.45); }
  }
`;

// Battery Gauge SVG component
const BatteryGauge = ({ percent, size = 48 }) => {
  const color = percent > 50 ? '#22c55e' : percent > 20 ? '#eab308' : '#ef4444';
  const radius = (size - 4) / 2;
  const circumference = Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg width={size} height={size / 2 + 4} viewBox={`0 0 ${size} ${size / 2 + 4}`}>
      <path
        d={`M 2,${size / 2 + 2} A ${radius},${radius} 0 0 1 ${size - 2},${size / 2 + 2}`}
        fill="none" stroke="var(--border-light)" strokeWidth="3" strokeLinecap="round"
      />
      <path
        d={`M 2,${size / 2 + 2} A ${radius},${radius} 0 0 1 ${size - 2},${size / 2 + 2}`}
        fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text
        x={size / 2}
        y={size / 2 - 1}
        textAnchor="middle"
        fontSize="10"
        fontWeight="700"
        fill={color}
      >
        {percent}%
      </text>
    </svg>
  );
};

const CockpitPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [renderTime, setRenderTime] = useState(0);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [cityFilter, setCityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [lockData, setLockData] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const navigate = useNavigate();

  // Pending extensions auto-polling (15s)
  const [pendingExtensions, setPendingExtensions] = useState({});
  const extTimerRef = useRef(null);

  useEffect(() => {
    const fetchPending = () => {
      axios.get(`${API_BASE_URL}/pending-extensions`)
        .then(r => r.data || {})
        .then(data => setPendingExtensions(data))
        .catch(() => {});
    };
    fetchPending();
    extTimerRef.current = setInterval(fetchPending, 15000);
    return () => { if (extTimerRef.current) clearInterval(extTimerRef.current); };
  }, []);

  const measureRenderTime = async (fn) => {
    const start = performance.now();
    await fn();
    const end = performance.now();
    setRenderTime((end - start) / 1000);
  };

  useEffect(() => {
    measureRenderTime(fetchPropertiesAndStatuses);
    getLockStatus().then(setLockData).catch(() => setLockData(null));
  }, []);

  const fetchPropertiesAndStatuses = async () => {
    setLoading(true);
    try {
      const propertiesResponse = await getPropertiesDashboard();
      const updatedProperties = await Promise.all(
        propertiesResponse.map(async (property) => {
          let status = 'Unknown';
          if (property.ical_url) {
            status = await getPropertyStatus(property.id);
          }
          return { ...property, status };
        })
      );
      setProperties(updatedProperties);

    } catch (err) {
      console.error("Error fetching properties or statuses:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatuses = async () => {
    setRefreshing(true);
    await measureRenderTime(async () => {
      try {
        const updatedProperties = await Promise.all(
          properties.map(async (property) => {
            let status = 'Unknown';
            if (property.ical_url) {
              await runCalendarScript(property.id);
              status = await getPropertyStatus(property.id);
            }
            return { ...property, status };
          })
        );
        setProperties(updatedProperties);
      } catch (err) {
        console.error("Error refreshing statuses:", err);
      }
    });
    setRefreshing(false);
  };

  const getStatusStyle = (status) => {
    const statusLower = status?.toLowerCase() || 'unknown';
    if (statusLower === 'occupied') return { ...styles.statusBadge, ...styles.statusOccupied };
    if (statusLower === 'vacant') return { ...styles.statusBadge, ...styles.statusVacant };
    return { ...styles.statusBadge, ...styles.statusUnknown };
  };

  const getStatColor = (type) => {
    if (type === 'occupied') return '#dc2626';
    if (type === 'vacant') return '#059669';
    if (type === 'unknown') return '#6b7280';
    return 'var(--color-primary)';
  };

  // Calculate stats
  const stats = {
    total: properties.length,
    occupied: properties.filter(p => p.status?.toLowerCase() === 'occupied').length,
    vacant: properties.filter(p => p.status?.toLowerCase() === 'vacant').length,
    unknown: properties.filter(p => !p.status || p.status?.toLowerCase() === 'unknown').length,
  };

  // Get unique cities and types for filter dropdowns (trim to avoid duplicates from whitespace)
  const uniqueCities = [...new Set(properties.map(p => p.city?.trim()).filter(Boolean))].sort();
  const uniqueTypes = [...new Set(properties.map(p => p.property_type?.trim()).filter(Boolean))].sort();

  // Filter and search
  const filteredProperties = properties.filter(property => {
    const matchesStatus = filter === 'all' || property.status?.toLowerCase() === filter;
    const matchesCity = cityFilter === 'all' || property.city?.trim() === cityFilter;
    const matchesType = typeFilter === 'all' || property.property_type?.trim() === typeFilter;
    const matchesSearch = !searchTerm ||
      property.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.city?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesCity && matchesType && matchesSearch;
  });

  if (loading) {
    return <LoadingScreen message="Loading dashboard..." />;
  }

  return (
    <div className="ayni-page">
      <style>{animationKeyframes}</style>

      {/* Header */}
      <div className="ayni-head">
        <h1 className="ayni-display-lg" style={{ margin: 0 }}>Cockpit</h1>
        <p className="ayni-statline" style={{ marginTop: 8 }}>
          <strong>{stats.occupied}</strong>occupied
          <span className="ayni-sep">·</span>
          <strong>{stats.vacant}</strong>vacant
        </p>
      </div>

      {/* Toolbar: tabs + search + view mode */}
      <div className="ayni-toolbar">
        <div className="ayni-tabs" style={{ margin: 0 }}>
          <button className={`ayni-tab ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>All <span className="ayni-count">{stats.total}</span></button>
          <button className={`ayni-tab tone-red ${filter === 'occupied' ? 'on' : ''}`} onClick={() => setFilter('occupied')}>Occupied <span className="ayni-count">{stats.occupied}</span></button>
          <button className={`ayni-tab tone-green ${filter === 'vacant' ? 'on' : ''}`} onClick={() => setFilter('vacant')}>Vacant <span className="ayni-count">{stats.vacant}</span></button>
        </div>
        <div className="ayni-toolbar-group">
          <input
            className="ayni-search"
            placeholder="Search properties…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            className="ayni-gear"
            onClick={handleRefreshStatuses}
            disabled={refreshing}
            title="Refresh statuses"
            aria-label="Refresh"
          >
            <i className={`fa-solid fa-arrows-rotate ${refreshing ? 'fa-spin' : ''}`} />
          </button>
          <div className="ayni-seg">
            <button className={viewMode === 'list' ? 'on' : ''} onClick={() => setViewMode('list')}>List</button>
            <button className={viewMode === 'grid' ? 'on' : ''} onClick={() => setViewMode('grid')}>Grid</button>
          </div>
        </div>
      </div>

      {/* Properties */}
      {filteredProperties.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No properties found matching your criteria.</p>
        </div>
      ) : viewMode === 'list' ? (
        /* ─── LIST VIEW ─── */
        <div className="ayni-card">
        <table className="ayni-table">
          <thead>
            <tr>
              <th style={{ width: 68 }}></th>
              <th>Property</th>
              <th>Location</th>
              <th>Status</th>
              <th>Lock</th>
              <th>Aynista</th>
            </tr>
          </thead>
          <tbody>
            {filteredProperties.map((property) => {
              const lock = property.seam_device_id && lockData?.locks?.find(l => l.device_id === property.seam_device_id);
              const pct = lock?.battery_percent ?? null;
              const statusLower = property.status?.toLowerCase() || 'unknown';
              const statusClass = statusLower === 'occupied' ? 'occupied' : statusLower === 'vacant' ? 'vacant' : 'quiet';
              const statusLabel = property.status ? (property.status[0].toUpperCase() + property.status.slice(1).toLowerCase()) : 'Unknown';
              const assignedCleaner = property.assigned_cleaner_name || property.aynista_name;
              const initial = assignedCleaner ? assignedCleaner.trim()[0].toUpperCase() : '';

              return (
                <tr key={property.id} onClick={() => navigate(`/properties/${property.id}`)}>
                  <td style={{ width: 68, padding: '16px 20px 16px 0' }}>
                    {property.image_url ? (
                      <img src={getPropertyImageUrl(property.image_url)} alt="" className="ayni-prop-thumb" />
                    ) : (
                      <div className="ayni-prop-thumb" />
                    )}
                  </td>
                  <td>
                    <div className="ayni-prop-name">{property.name}</div>
                    <div className="ayni-prop-addr">{property.address || ''}</div>
                  </td>
                  <td className="ayni-meta">
                    {property.city || '—'}<span className="ayni-sep">/</span>{property.property_type || '—'}
                  </td>
                  <td><span className={`ayni-status ${statusClass}`}>{refreshing ? '…' : statusLabel}</span></td>
                  <td>
                    {pct != null ? (
                      <span className="ayni-lock">
                        <span className="ayni-lock-bar"><span className="ayni-lock-bar-fill" style={{ width: `${pct}%` }} /></span>
                        {pct}%
                      </span>
                    ) : (
                      <span style={{ color: 'var(--ayni-ink-4)' }}>—</span>
                    )}
                  </td>
                  <td>
                    {assignedCleaner ? (
                      <span className="ayni-aynista">
                        <span className="ayni-aynista-dot">{initial}</span>
                        {assignedCleaner}
                      </span>
                    ) : (
                      <span className="ayni-aynista unassigned">
                        <span className="ayni-aynista-dot"></span>
                        unassigned
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      ) : (
        /* ─── GRID VIEW ─── */
        <div style={styles.grid}>
          {filteredProperties.map((property) => {
            const lock = property.seam_device_id && lockData?.locks?.find(l => l.device_id === property.seam_device_id);
            const hasCleaner = property.current_or_upcoming_reservation;

            return (
              <div
                key={property.id}
                style={{
                  ...styles.card,
                  ...(hoveredCard === property.id ? styles.cardHover : {}),
                }}
                onClick={() => navigate(`/properties/${property.id}`)}
                onMouseEnter={() => setHoveredCard(property.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Property Image */}
                <div style={styles.cardImageContainer}>
                  {property.image_url ? (
                    <img
                      src={getPropertyImageUrl(property.image_url)}
                      alt={property.name}
                      style={{
                        ...styles.cardImage,
                        ...(hoveredCard === property.id ? styles.cardImageHover : {}),
                      }}
                    />
                  ) : (
                    <div style={styles.cardImagePlaceholder}>
                      <i className="fa-solid fa-building" style={{ fontSize: '32px', color: 'var(--text-muted)' }} />
                    </div>
                  )}
                  <div style={styles.imageOverlay} />
                  <span style={{
                    ...styles.statusBadgeOverlay,
                    ...(property.status?.toLowerCase() === 'occupied'
                      ? { backgroundColor: 'rgba(220,38,38,0.85)', color: '#fff' }
                      : property.status?.toLowerCase() === 'vacant'
                      ? { backgroundColor: 'rgba(5,150,105,0.85)', color: '#fff' }
                      : { backgroundColor: 'rgba(107,114,128,0.85)', color: '#fff' }),
                  }}>
                    {refreshing ? '...' : property.status || 'Unknown'}
                  </span>
                </div>

                <div style={styles.cardContent}>
                  <div style={{ ...styles.cardHeader, marginBottom: '12px' }}>
                    <div style={styles.propertyInfo}>
                      <h3 style={styles.propertyName}>
                        {property.name}
                        {pendingExtensions[property.id]?.length > 0 && (
                          <span style={{
                            marginLeft: '8px', fontSize: '10px', fontWeight: '600',
                            backgroundColor: '#fff7ed', color: '#9a3412', border: '1px solid #f97316',
                            borderRadius: '6px', padding: '2px 8px', verticalAlign: 'middle',
                          }}>Extension pending</span>
                        )}
                      </h3>
                      <p style={styles.propertyAddress}>{property.address}</p>
                    </div>
                  </div>

                    {/* Compact info line */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                    <span><i className="fa-solid fa-location-dot" style={{ fontSize: '10px', marginRight: '4px' }} />{property.city || '-'}</span>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span>{property.property_type || '-'}</span>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span><i className="fa-regular fa-clock" style={{ fontSize: '10px', marginRight: '3px' }} />{property.check_out_time || '11:00'} - {property.check_in_time || '15:00'}</span>
                  </div>

                  {/* Lock + Cleaner section — always rendered for consistent card height */}
                  <div style={{
                    marginTop: '10px', padding: '8px 12px', borderRadius: '8px',
                    backgroundColor: 'var(--bg-subtle)', display: 'flex', alignItems: 'center', gap: '12px',
                    ...(lock && (lock.battery_percent ?? 0) < 20 ? { animation: 'lowBatteryPulse 2s ease-in-out infinite' } : {}),
                  }}>
                    {lock ? (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <BatteryGauge percent={lock.battery_percent ?? 0} size={48} />
                          <i className={lock.locked ? 'fa-solid fa-lock' : 'fa-solid fa-lock-open'} style={{ fontSize: '12px', color: 'var(--text-muted)' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {lock.name}
                          </span>
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            {lock.locked ? 'Locked' : 'Unlocked'}
                            {!lock.online && <span style={{ color: '#ef4444', fontWeight: '700', marginLeft: '6px' }}>OFFLINE</span>}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                        <i className="fa-solid fa-lock" style={{ fontSize: '14px', color: 'var(--border-light)' }} />
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No lock assigned</span>
                      </div>
                    )}
                  </div>

                  <div style={{
                    ...styles.cleanerRow,
                    ...(hasCleaner && property.assigned_cleaner_name ? styles.cleanerAssigned : hasCleaner ? styles.cleanerUnassigned : {
                      backgroundColor: 'var(--bg-subtle)', color: 'var(--text-muted)', borderLeftColor: 'var(--border-light)',
                    }),
                  }}>
                    {hasCleaner ? (
                      property.assigned_cleaner_name ? (
                        <span><i className="fa-solid fa-user" style={{ marginRight: '6px', fontSize: '11px' }} />{property.assigned_cleaner_name}</span>
                      ) : (
                        <span><i className="fa-solid fa-user-slash" style={{ marginRight: '6px', fontSize: '11px' }} />Unassigned</span>
                      )
                    ) : (
                      <span style={{ fontStyle: 'italic', fontSize: '12px' }}><i className="fa-regular fa-calendar" style={{ marginRight: '6px', fontSize: '11px' }} />No upcoming booking</span>
                    )}
                    {hasCleaner && property.reservation_check_out && (
                      <span style={styles.assignmentDate}>
                        <i className="fa-regular fa-calendar" style={{ marginRight: '4px', fontSize: '10px' }} />
                        {(() => {
                          const [year, month, day] = property.reservation_check_out.split('-');
                          return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        })()}
                      </span>
                    )}
                  </div>

                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Render Time */}
      <div style={styles.renderTime}>
        Last refresh: {renderTime.toFixed(2)}s
      </div>
    </div>
  );
};

export default CockpitPage;
